import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { WorkshopDefectPart } from '../../models/workshop-defect-part.model';
import { WorkshopDefectPartService } from '../../services/workshop-defect-part.service';
import { Product } from '../../../order/models/product.model';
import { ProductService } from '../../../quoting/services/product.service';
import { catchError, take } from 'rxjs/operators';

@Component({
    selector: 'app-workshop-defect-parts',
    templateUrl: './workshop-defect-parts.component.html',
    styleUrls: ['./workshop-defect-parts.component.scss'],
    standalone: false
})
export class WorkshopDefectPartsComponent implements OnInit {

  @Input()
  workshopDefectId: number;

  // 'live' (default) - this defect already exists, so a part is assigned/removed on the backend
  // immediately (moves real stock straight away). 'local' - the defect doesn't exist yet (the New
  // page), so parts are only held here in memory; nothing is posted until the parent creates the
  // defect and replays localParts against the real assignPart endpoint.
  @Input()
  mode: 'live' | 'local' = 'live';

  @Input()
  localParts: WorkshopDefectPart[] = [];

  @Output()
  localPartsChange = new EventEmitter<WorkshopDefectPart[]>();

  parts: WorkshopDefectPart[] = [];
  products: Product[] = [];

  newProductId: number = -1;
  newQty: number = 1;

  private nextLocalId: number = -1;

  constructor(
    private workshopDefectPartService: WorkshopDefectPartService,
    private productService: ProductService) { }

  ngOnInit(): void {
    if(this.mode === 'live') {
      this.loadParts();
    }

    this.productService.getAllActiveProducts()
    .pipe(catchError((e) => { alert('Products could not be loaded'); return e; }))
    .pipe(take(1))
    .subscribe((products: Product[]) => {
      this.products = products;
      this.products.push({id: -1, name: 'Select a part...'} as any);
    });
  }

  displayParts(): WorkshopDefectPart[] {
    return this.mode === 'local' ? this.localParts : this.parts;
  }

  loadParts(): void {
    this.workshopDefectPartService.getForDefect(this.workshopDefectId)
    .pipe(take(1))
    .subscribe((parts: WorkshopDefectPart[]) => {
      this.parts = parts;
    });
  }

  addPart(): void {
    if(this.newProductId === -1 || !this.newQty || this.newQty <= 0) {
      return;
    }

    if(this.mode === 'local') {
      const product = this.products.find(p => p.id === this.newProductId);

      this.localParts = [...this.localParts, {
        id: this.nextLocalId--,
        productId: this.newProductId,
        product: product,
        qty: this.newQty,
      } as WorkshopDefectPart];
      this.localPartsChange.emit(this.localParts);

      this.newProductId = -1;
      this.newQty = 1;
      return;
    }

    this.workshopDefectPartService.assignPart(this.workshopDefectId, this.newProductId, this.newQty)
    .pipe(take(1))
    .pipe(catchError((e) => {
      alert(e && e.error && e.error.message ? e.error.message : 'Could not assign this part.');
      return e;
    }))
    .subscribe((result) => {
      if(result instanceof HttpErrorResponse) {
        return;
      }
      this.newProductId = -1;
      this.newQty = 1;
      this.loadParts();
    });
  }

  removePart(part: WorkshopDefectPart): void {
    if(this.mode === 'local') {
      this.localParts = this.localParts.filter(p => p.id !== part.id);
      this.localPartsChange.emit(this.localParts);
      return;
    }

    this.workshopDefectPartService.removePart(part.id)
    .pipe(take(1))
    .pipe(catchError((e) => {
      alert(e && e.error && e.error.message ? e.error.message : 'Could not remove this part.');
      return e;
    }))
    .subscribe(() => {
      this.loadParts();
    });
  }

}
